import React from "react";
import { ContactType } from "@akfdev-com/shared";
import { computeModuleLayout } from "./moduleLayout";
import type { ModuleId } from "./types";

interface ContactModulesProps {
  type: ContactType | null | undefined;
  children: Partial<Record<ModuleId, React.ReactNode>> & {
    legend?: React.ReactNode;
  };
}

/**
 * Contact Modules Wrapper
 *
 * Renders type-aware modules in a responsive two-column grid layout:
 * - LEFT COLUMN: Contact Information + Financial Records (invoices key)
 * - RIGHT COLUMN: Billing & Financial Summary + Service Charges + Default Service Charges/Pricing + Module-state Legend
 * - Stacks to one column on mobile (responsive)
 *
 * Uses `computeModuleLayout` as the single source of module state: active / greyed / hidden.
 * Does not fork per-view logic; each module renders based on state from computeModuleLayout.
 *
 * - Active: visible and interactive
 * - Greyed: visible but disabled (~60% opacity, aria-disabled, removed from tab order, non-focusable)
 * - Hidden: not rendered (absent from DOM)
 *
 * When type is null/unknown/out-of-set, shows only the universally-active modules and surfaces
 * an unrecognised-type indication.
 *
 * Validates: Requirements 5.1, 5.3, 5.4, 5.5, 5.6, 5.7
 */
export const ContactModules: React.FC<ContactModulesProps> = ({
  type,
  children,
}) => {
  const layout = computeModuleLayout(type);

  const renderModule = (moduleId: ModuleId, content: React.ReactNode) => {
    const state = layout.states[moduleId];

    if (state === "hidden") {
      return null;
    }

    if (state === "greyed") {
      return (
        <div
          key={moduleId}
          className="opacity-60 pointer-events-none"
          aria-disabled="true"
          role="region"
          tabIndex={-1}
        >
          {content}
        </div>
      );
    }

    // state === 'active'
    return (
      <div key={moduleId} role="region">
        {content}
      </div>
    );
  };

  // Left column modules: Contact Information + Financial Records
  const leftColumnModules: ModuleId[] = [
    "contactInformation",
    "invoices",
    "bills",
  ];

  // Right column modules: Billing & Financial Summary + Service Charges + Default Service Charges
  const rightColumnModules: ModuleId[] = [
    "billingFinancialSummary",
    "serviceCharges",
    "defaultServiceCharges",
  ];

  // Universal modules (rendered below grid): Notes, Documents, History
  const universalModules: ModuleId[] = ["notes", "documents", "history"];

  return (
    <div className="space-y-6">
      {/* Unrecognised type warning */}
      {layout.unrecognisedType && (
        <div className="rounded-md bg-yellow-50 border border-yellow-200 px-4 py-3">
          <p className="text-sm text-yellow-800">
            Contact type is unrecognised. Only essential modules are displayed.
          </p>
        </div>
      )}

      {/* Two-column grid: responsive, md+ shows 2 columns, mobile shows 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* LEFT COLUMN: Contact Information + Financial Records */}
        <div className="space-y-6">
          {leftColumnModules.map((moduleId) => {
            const content = children[moduleId];
            if (!content) {
              return null;
            }
            return renderModule(moduleId, content);
          })}
        </div>

        {/* RIGHT COLUMN: Billing & Financial Summary + Service Charges + Default Service Charges + Legend */}
        <div className="space-y-6">
          {rightColumnModules.map((moduleId) => {
            const content = children[moduleId];
            if (!content) {
              return null;
            }
            return renderModule(moduleId, content);
          })}

          {/* Module-state Legend slot - will be populated by parent */}
          {children.legend && <div role="region">{children.legend}</div>}
        </div>
      </div>

      {/* Universal modules (below grid): Notes, Documents, History */}
      <div className="space-y-6">
        {universalModules.map((moduleId) => {
          const content = children[moduleId];
          if (!content) {
            return null;
          }
          return renderModule(moduleId, content);
        })}
      </div>
    </div>
  );
};
